import { CircularProgress, Grid, Stack } from '@mui/material';
import ServiceFormCalendarAvailability from 'app/calendarV4/services/serviceForm/ServiceFormCalendarAvailability';
import EventItemCarousel from 'app/event/eventForm/common/EventItemCarousel';
import EmptyTypes from 'common/enums/emptyTypes';
import SearchInput from 'common/features/filters/components/SearchInput';
import EmptyView from 'common/features/noData/EmptyView';
import useFlexBoxStyles from 'common/flexBox';
import LayoutSubForm from 'common/layouts/LayoutSubForm';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch } from 'react-redux';
import { fetchCalendarsAction } from 'redux/slices/iCalendarV4';
import { closeNestedDialogAction, openNestedDialog } from 'redux/slices/nestedDialogSlice';
import { makeStyles } from 'tss-react/mui';

import CalendarType from 'common/enums/calendarTypes';
import { useSnackbar } from 'notistack';
import ServiceFormCalendarItem from './ServiceFormCalendarItem';

const PAGE_SIZE = 20;

const useStyles = makeStyles()((theme) => ({
    selectedContent: {
        minHeight: theme.spacing(25),
        height: theme.spacing(25),
        [theme.breakpoints.up('xl')]: {
            minHeight: theme.spacing(30),
            height: theme.spacing(30),
        },
    },
    selectCalendarsScroll: {
        height: 'calc(100% - 60px)',
        minHeight: 'calc(100% - 96px)',
    },
}));

const ServiceFormSelectCalendars = (props) => {
    const { type, selectedCalendars, close, onConfirm, emptyType, sectionName, userGroupId } = props;

    const { t } = useTranslation();

    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();
    const [showCalendars, setShowCalendars] = useState([]);
    const [selected, setSelected] = useState(selectedCalendars || []);
    const [search, setSearch] = useState('');
    const [initialSearch, setInitialSearch] = useState('');
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const [wasSearching, setWasSearching] = useState(false);
    const [error, setError] = useState(false);
    const [calendars, setCalendars] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const scrollRef = useRef();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    const editAvailability = (availability, calendar) => {
        const index = selected.findIndex((b) => b.id === calendar.id);
        const selectedTemp = [...selected.slice(0, index), { ...calendar, availability }, ...selected.slice(index + 1)];
        setSelected(selectedTemp);
        dispatch(closeNestedDialogAction());
    };

    const editCalendar = (calendar) => {
        dispatch(
            openNestedDialog(
                <ServiceFormCalendarAvailability
                    calendar={calendar}
                    handleSubmit={(availability) => editAvailability(availability, calendar)}
                />,
            ),
        );
    };

    const addCalendars = (id) => {
        const calendar = calendars.find((calendar) => calendar.id === id);
        const selectedTemp = [{ ...calendar, active: true }, ...selected];
        setSelected(selectedTemp);
        const showCalendarsTemp = calendars.filter((a) => !selectedTemp.some((b) => b.id === a.id));
        setShowCalendars(showCalendarsTemp);
    };

    const deleteCalendar = (calendar) => {
        const index = selected.findIndex((b) => b.id === calendar.id);
        const calendarsTemp = [...selected.slice(0, index), ...selected.slice(index + 1)];
        setSelected(calendarsTemp);
        const showCalendarsTemp = [calendar, ...showCalendars];
        setShowCalendars(showCalendarsTemp);
    };

    const done = (event) => {
        event.preventDefault();
        onConfirm(selected);
        close();
    };

    const renderCalendar = ({
        calendar,
        disable = false,
        deleteButton = false,
        editButton = false,
        enableOnClick = false,
    }) => {
        return (
            <Grid key={calendar.id} item mg={4} xl={3} lg={4} md={6} sm={6} xs={6}>
                {/* <CustomTooltip arrow disableHoverListener={!disable} title={t('common.utils.const.notAuthorize')}> */}
                <ServiceFormCalendarItem
                    enableOnClick={enableOnClick && !disable}
                    onClick={() => addCalendars(calendar.id)}
                    calendar={calendar}
                    deleteCalendar={deleteCalendar}
                    deleteButton={deleteButton}
                    editCalendar={editCalendar}
                    editButton={editButton}
                />
                {/* </CustomTooltip> */}
            </Grid>
        );
    };

    const emptyDescription =
        type === CalendarType.AGENT ? t('service.form.emptyAgents') : t('service.form.emptyAssets');
    const emptyDescriptionNoCalendars =
        type === CalendarType.AGENT ? t('service.form.noAgents') : t('service.form.noAssets');

    const getItems = ({ array, deleteButton, editButton, enableOnClick }) => {
        return array.map((calendar, index) => (
            <React.Fragment key={index + 1}>
                {renderCalendar({ calendar, disable: false, deleteButton, editButton, enableOnClick })}
            </React.Fragment>
        ));
    };

    const disableSubmit =
        type === CalendarType.AGENT && selected.findIndex((calendar) => !calendar.availability) !== -1;

    const loadData = useCallback(
        (offset) => {
            setLoading(true);
            dispatch(
                fetchCalendarsAction({
                    queryParams: {
                        page: offset + 1,
                        limit: PAGE_SIZE,
                        type,
                        search,
                        userGroupId,
                    },
                    onSuccess: (response) => {
                        setInitialLoadDone(true);
                        setError(false);
                        setCalendars((prev) => {
                            return [...(offset === 0 ? [] : prev), ...response.items];
                        });
                        setShowCalendars((prev) => {
                            return [
                                ...(offset === 0 ? [] : prev),
                                ...response.items.filter((a) => !selected.some((b) => b.id === a.id)),
                            ];
                        });
                        setCurrentPage(response.currentPage);
                        setTotalPages(response.totalPages);
                        setLoading(false);
                        setHasMore(response.currentPage < response.totalPages);
                    },
                    onFailure: () => {
                        setError(true);
                        setLoading(false);
                        enqueueSnackbar(t('app.common.errorResponse'), {
                            variant: 'error',
                        });
                    },
                    options: { useState: false },
                }),
            );
        },
        [dispatch, search, selected, type, userGroupId, enqueueSnackbar, t],
    );

    const handleLoadMore = () => {
        if (currentPage < totalPages && !loading) {
            loadData(currentPage);
        }
    };

    useEffect(() => {
        if (!initialLoadDone && !loading && !error) {
            loadData(0);
        }
    }, [loadData, initialLoadDone, loading, error]);

    useEffect(() => {
        if (initialSearch !== search) {
            setWasSearching(true);
            setInitialSearch(search);
            loadData(0);
        }
    }, [search, wasSearching, loadData, initialSearch]);

    const getScrollParentSafe = useCallback(() => {
        return scrollRef.current ?? undefined;
    }, []);

    return (
        <LayoutSubForm
            sectionName={sectionName}
            close={close}
            done={done}
            showCloseButton={false}
            disabled={disableSubmit}
        >
            <Stack className={cx(classes.selectedContent, flexBox.relative)}>
                {Boolean(selected?.length) && (
                    <EventItemCarousel upcomingLength={selected?.length}>
                        <Grid container spacing={2} wrap="nowrap">
                            {getItems({ array: selected, deleteButton: true, editButton: true, enableOnClick: false })}
                        </Grid>
                    </EventItemCarousel>
                )}

                {!selected?.length && <EmptyView size="small" type={emptyType} description={emptyDescription} />}
            </Stack>

            <Stack direction="column" className={cx(flexBox.overflowXHidden, flexBox.grow1)}>
                <Stack
                    direction="column"
                    className={cx(flexBox.scrollbarNone, flexBox.overflowXHidden, flexBox.h100)}
                    spacing={2}
                >
                    <SearchInput onChange={(e) => setSearch(e)} onClick={() => {}} fullWidth={true} />
                    <Stack direction="column" className={cx(flexBox.overflowHidden, flexBox.h100, flexBox.relative)}>
                        {calendars.length === 0 && loading && (
                            <Stack
                                direction="row"
                                justifyContent="center"
                                alignItems="center"
                                className={cx(flexBox.zIndex8, flexBox.h100, flexBox.w100, flexBox.absolute)}
                            >
                                <CircularProgress />
                            </Stack>
                        )}
                        <Stack
                            direction="column"
                            className={cx(flexBox.scrollbarNone, flexBox.overflowAuto)}
                            // className={cx(flexBox.scrollbarNone, flexBox.overflowAuto)}
                            ref={scrollRef}
                        >
                            <InfiniteScroll
                                getScrollParent={getScrollParentSafe}
                                loadMore={handleLoadMore}
                                hasMore={hasMore}
                                useWindow={false}
                            >
                                <Grid container spacing={2}>
                                    {getItems({ array: showCalendars, enableOnClick: true })}
                                </Grid>
                            </InfiniteScroll>
                        </Stack>
                        {!calendars?.length && !loading && (
                            <EmptyView
                                size="small"
                                noImage={true}
                                type={search ? EmptyTypes.EMPTY_SEARCH : ''}
                                description={emptyDescriptionNoCalendars}
                            />
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </LayoutSubForm>
    );
};

export default ServiceFormSelectCalendars;
